import React from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { Touchable } from '../common/Touchable';
import { BaseText } from '../common/BaseText';
import { Button, ButtonStyleEnum } from '../common/Button';
import { AvatarImage } from '../common/AvatarImage';
import { Separator } from '../common/Separator';
import { Icon } from '../common/Icon';
import type { User } from '@models/Viewer/UserModel';

const TouchableContainer = styled(Touchable)({
  backgroundColor: '$background0',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const TextContainer = styled(View)({
  px: '$5',
  flexGrow: 1,
  flexShrink: 1,
});

const Title = styled(BaseText)({
  variant: 'text.medium',
  fontWeight: 'semi',
  color: '$heading800',
});

const Subtitle = styled(BaseText)({
  pl: '$2',
  variant: 'text.small',
  color: '$text80',
});

const FollowButton = styled(Button)({
  px: '$7',
});

interface IFriendshipUserItemProps {
  item: User;
  onPress(item: User): void;
  isShowLocation?: boolean;
}

export const FriendshipUserItem = observer<IFriendshipUserItemProps>(
  ({ item, onPress, isShowLocation }) => {
    const { t } = useTranslation();

    async function toggleFollow() {
      try {
        await item.toggleFollow.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    const buttonStyle = item.isFollowing
      ? ButtonStyleEnum.Outline
      : ButtonStyleEnum.Flat;

    const buttonTitle = t('common.user.button', {
      context: item.isFollowing ? 'following' : 'follow',
    });

    return (
      <TouchableContainer onPress={() => onPress(item)}>
        <RowContainer>
          <AvatarImage
            size={64}
            source={{ uri: item.photo.normal?.url }}
          />

          <TextContainer>
            <Title numberOfLines={1}>{item.fullName}</Title>
            <Separator size="$2" />

            {isShowLocation && (
              <RowContainer>
                <Icon name="location-light" size={20} />
                <Subtitle>{item.location}</Subtitle>
              </RowContainer>
            )}
          </TextContainer>

          <View>
            <FollowButton
              disabled={item.isViewer}
              buttonStyle={buttonStyle}
              onPress={toggleFollow}
            >
              {buttonTitle}
            </FollowButton>
          </View>
        </RowContainer>
      </TouchableContainer>
    );
  },
);
